import Hls, { FragChangedData } from 'hls.js';
import { useCallback } from 'react';
import Player from 'video.js/dist/types/player';

/**
 * Sets up HLS player to sync wall-clock time from stream PDT (Program Date Time) and
 * call onClockUpdate on each timeupdate. Uses FRAG_CHANGED to get segment start time
 * and interpolates current time from playhead position.
 */
export function useHlsClockSync(onClockUpdate?: (time: Date) => void) {
  const onReady = useCallback(
    (player: Player) => {
      player.el().classList.add('vjs-no-pause');

      const hls = (player as { hls?: Hls }).hls;
      if (!hls) return;

      let currentFrag: FragChangedData['frag'] | null = null;
      let timeupdateAttached = false;

      const updateClock = () => {
        const media = hls.media;
        if (!currentFrag?.programDateTime || !media) return;

        const driftOffset = (media.currentTime - currentFrag.start) * 1000;
        onClockUpdate?.(new Date(currentFrag.programDateTime + driftOffset));
      };

      hls.on(Hls.Events.FRAG_CHANGED, (_event: unknown, data: FragChangedData) => {
        const frag = data.frag;
        const media = hls.media;
        if (!frag?.programDateTime || !media) return;

        currentFrag = frag;

        if (!timeupdateAttached) {
          timeupdateAttached = true;
          media.addEventListener('timeupdate', updateClock);
          player.one('dispose', () => media.removeEventListener('timeupdate', updateClock));
        }
      });
    },
    [onClockUpdate],
  );

  return onReady;
}
